/**
 * OwnerSettings Service
 * 오너 설정 비즈니스 로직
 */
import { OrganizationInfo } from '../models/OwnerSettings';

export class OwnerSettingsService {
  /**
   * 조직 ID를 클립보드에 복사
   */
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('클립보드 복사 실패:', error);
      return false;
    }
  }

  /**
   * localStorage에서 조직명 가져오기
   */
  getSavedOrganizationName(): string | null {
    const organization = localStorage.getItem('organization');
    if (organization) {
      return organization.split(' ')[0];
    }
    return null;
  }

  /**
   * localStorage의 조직명 업데이트
   */
  updateOrganizationNameInStorage(newName: string): void {
    const currentOrg = localStorage.getItem('organization') || '';
    const orgParts = currentOrg.split(' ');
    orgParts[0] = newName;
    localStorage.setItem('organization', orgParts.join(' '));
  }

  /**
   * 조직 정보를 localStorage에 캐싱
   */
  cacheOrganizationInfo(orgInfo: OrganizationInfo): void {
    try {
      localStorage.setItem('cached_organization_info', JSON.stringify(orgInfo));
    } catch (e) {
      console.error('조직 정보 캐싱 실패:', e);
    }
  }

  /**
   * localStorage에서 캐싱된 조직 정보 가져오기
   */
  getCachedOrganizationInfo(): OrganizationInfo | null {
    try {
      const cached = localStorage.getItem('cached_organization_info');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error('캐싱된 조직 정보 조회 실패:', e);
    }
    return null;
  }

  /**
   * user-storage에서 조직명 업데이트 (zustand persist 호환)
   */
  updateUserStorageOrganizationName(newName: string): void {
    const userStorage = localStorage.getItem('user-storage');
    if (!userStorage) return;

    try {
      const parsed = JSON.parse(userStorage);
      if (parsed.state && parsed.state.currentUser) {
        parsed.state.currentUser.organization_name = newName;
        localStorage.setItem('user-storage', JSON.stringify(parsed));
      }
    } catch (e) {
      console.error('user-storage 업데이트 실패:', e);
    }
  }

  /**
   * 조직명 업데이트 이벤트 발생 (사이드바 리렌더링용)
   */
  dispatchOrganizationUpdateEvent(): void {
    window.dispatchEvent(new Event('organizationNameUpdated'));
  }

  /**
   * 사용자 정보에서 조직 ID 추출
   */
  extractOrganizationId(currentUser: any): string | number {
    return (
      currentUser?.organization_id ||
      currentUser?.org_id ||
      currentUser?.business_id ||
      localStorage.getItem('organization_id') || // localStorage 폴백
      'org_unknown'
    );
  }

  /**
   * 임시 조직 정보 생성 (실제 API 없을 때 사용)
   */
  createTempOrganizationInfo(
    orgId: string | number,
    currentUser: any,
    savedOrgName: string | null
  ): OrganizationInfo {
    return {
      id: orgId,
      name: savedOrgName || currentUser?.organization_name || 'OO독서실',
      description: 'NewAttend 시스템으로 운영되는 스마트 독서실입니다.',
      settings: {},
    };
  }

  /**
   * 시스템 상태 텍스트 반환
   */
  getSystemStatusText(status: 'active' | 'inactive' | 'connected' | 'disconnected'): string {
    const labels: Record<string, string> = {
      active: '활성화',
      inactive: '비활성',
      connected: '연결됨',
      disconnected: '연결 끊김',
    };
    return labels[status] || status;
  }

  /**
   * 시스템 상태 색상 클래스 반환
   */
  getSystemStatusColor(
    status: 'active' | 'inactive' | 'connected' | 'disconnected'
  ): string {
    const isActive = status === 'active' || status === 'connected';
    return isActive
      ? 'bg-green-50 border-green-200 text-green-800'
      : 'bg-red-50 border-red-200 text-red-800';
  }
}

export const ownerSettingsService = new OwnerSettingsService();
